<script setup lang="ts">
import { computed, toRefs } from "vue";
import { useI18n } from "vue-i18n";
import dayjs from "dayjs";
import FileDTO = App.Data.FileDTO;
import { useFileBrowserActions } from "@/ui/components/FileBrowser/useFileBrowserActions";
import { fileActionAvailability } from "@/ui/components/FileBrowser/actionAvailability";
import TagChip from "@/ui/components/FileBrowser/TagChip.vue";

const { t } = useI18n();
const { hasAction } = useFileBrowserActions();

/**
 * A dashboard reports its mode, a document the type of its active version.
 * Both are enums, so both are read out of their own message namespace.
 */
const kind = computed<{ label: string; value: string } | null>(() => {
	if (selectedFile.value?.mode) {
		return {
			label: t("fileBrowser.columns.mode"),
			value: t(`fileBrowser.modes.${selectedFile.value.mode}`),
		};
	}

	if (selectedFile.value?.view_tab) {
		return {
			label: t("fileBrowser.columns.view_tab"),
			value: t(`fileBrowser.viewTabs.${selectedFile.value.view_tab}`),
		};
	}

	return null;
});

const props = defineProps<{
	selectedFile: FileDTO;
	resourceTreeCategory?: "requested_documents" | "dashboards";
}>();

const { selectedFile, resourceTreeCategory } = toRefs(props);

const emit = defineEmits([
	"reload-files-in-selected-folder",
	"edit-selected-file",
	"close-properties",
	"share-resource",
	"remove-file",
	"toggle-favorite-resource",
]);

const removeFile = () => {
	emit("remove-file");
};

const toggleFavoriteResource = () => {
	emit("toggle-favorite-resource");
};

const editFile = () => {
	emit("edit-selected-file");
};

const closeProperties = () => {
	emit("close-properties");
};

const shareResource = () => {
	emit("share-resource");
};

const fileIcon = computed(() =>
	resourceTreeCategory.value === "dashboards"
		? "fa: fa-regular fa-file-spreadsheet"
		: "mdi-file-chart-outline"
);

const formatDate = (value: string | null | undefined): string =>
	value ? dayjs(value).format("DD MMM YYYY HH:mm") : "—";

/* Shared with the right-click menu, so both offer the same set. */
const availability = computed(() =>
	fileActionAvailability(selectedFile.value, hasAction)
);

const isStoredDocument = computed(() => {
	return (
		selectedFile.value?.resource_type &&
		selectedFile.value.resource_type === "App\\Models\\StoredDocument"
	);
});

/**
 * `!= null` on purpose: the counts below are optional on the DTO, so a listing
 * that does not carry them leaves them `undefined` rather than `null` - and
 * `!== null` would wave that straight through into a row reading "0".
 */
const isSharedVisible = computed(
	() =>
		selectedFile.value.count_users_with_abilities != null &&
		!!selectedFile.value.active_user_abilities &&
		selectedFile.value.active_user_abilities.includes("*")
);

const isUsedInVisible = computed(
	() =>
		selectedFile.value.dashboard_cards_count != null && isStoredDocument.value
);
</script>

<template>
	<div class="file-properties">
		<div class="file-properties__header pa-2">
			<v-icon
				size="16"
				class="file-properties__icon"
				:color="selectedFile.is_favorite ? 'warning' : undefined"
			>
				{{ fileIcon }}
			</v-icon>

			<span class="file-properties__title" :title="selectedFile.title">
				{{ selectedFile.title }}
			</span>

			<v-btn
				icon
				size="small"
				variant="text"
				density="comfortable"
				class="file-properties__close"
				@click="closeProperties"
			>
				<v-icon size="13">fa: fa-solid fa-xmark</v-icon>
				<v-tooltip activator="parent">
					{{ t("fileBrowser.properties.closeDetails") }}
				</v-tooltip>
			</v-btn>
		</div>

		<v-divider />

		<!-- Band 2: metadata -->
		<div class="file-properties__body pa-2">
			<div v-if="selectedFile.description" class="prop prop--stacked">
				<span class="prop__label">
					{{ t("fileBrowser.properties.description") }}
				</span>
				<span class="prop__value prop__value--wrap">
					{{ selectedFile.description }}
				</span>
			</div>

			<div class="prop">
				<span class="prop__label">
					{{ t("fileBrowser.properties.lastOpened") }}
				</span>
				<span class="prop__value">
					{{ formatDate(selectedFile.last_opened_at) }}
				</span>
			</div>

			<div class="prop">
				<span class="prop__label">
					{{ t("fileBrowser.properties.timesOpened") }}
				</span>
				<span class="prop__value">{{ selectedFile.times_opened ?? 0 }}</span>
			</div>

			<div v-if="isSharedVisible" class="prop">
				<span class="prop__label">
					{{ t("fileBrowser.properties.sharedWith") }}
				</span>
				<span class="prop__value">
					{{
						t(
							"fileBrowser.userCount",
							selectedFile.count_users_with_abilities ?? 0
						)
					}}
				</span>
			</div>

			<div v-if="isUsedInVisible" class="prop">
				<span class="prop__label">
					{{ t("fileBrowser.properties.usedIn") }}
				</span>
				<span class="prop__value">
					{{
						t(
							"fileBrowser.dashboardCount",
							selectedFile.dashboard_cards_count ?? 0
						)
					}}
				</span>
			</div>

			<v-divider class="my-2" />

			<div class="prop">
				<span class="prop__label">
					{{ t("fileBrowser.properties.created") }}
				</span>
				<span class="prop__value">
					{{ formatDate(selectedFile.created_at) }}
				</span>
			</div>

			<div v-if="selectedFile.last_modified_at" class="prop">
				<span class="prop__label">
					{{ t("fileBrowser.properties.modified") }}
				</span>
				<span class="prop__value">
					{{ formatDate(selectedFile.last_modified_at) }}
				</span>
			</div>

			<div v-if="selectedFile.last_modified_by?.name" class="prop">
				<span class="prop__label">
					{{ t("fileBrowser.properties.modifiedBy") }}
				</span>
				<span class="prop__value" :title="selectedFile.last_modified_by.name">
					{{ selectedFile.last_modified_by.name }}
				</span>
			</div>

			<div v-if="selectedFile.user?.name" class="prop">
				<span class="prop__label">
					{{ t("fileBrowser.properties.owner") }}
				</span>
				<span class="prop__value" :title="selectedFile.user.name">
					{{ selectedFile.user.name }}
				</span>
			</div>

			<v-divider class="my-2" />

			<div v-if="kind" class="prop">
				<span class="prop__label">{{ kind.label }}</span>

				<v-chip size="x-small">{{ kind.value }}</v-chip>
			</div>

			<div v-if="selectedFile.tags?.length" class="prop">
				<span class="prop__label">
					{{ t("fileBrowser.properties.tags") }}
				</span>
				<span class="prop__value prop__value--tags">
					<tag-chip
						v-for="tag in selectedFile.tags"
						:key="tag.id"
						:name="tag.name"
						:rgb-color-value="tag.rgb_color_value"
					/>
				</span>
			</div>
		</div>

		<!-- Band 3: actions -->
		<template v-if="availability.any">
			<v-divider />

			<div class="file-properties__footer pa-2">
				<v-spacer />

				<v-btn
					v-if="availability.edit"
					icon
					size="small"
					variant="text"
					density="comfortable"
					@click="editFile"
				>
					<v-icon size="13">fa: fa-solid fa-pen</v-icon>
					<v-tooltip activator="parent">
						{{ t("fileBrowser.properties.editTooltip") }}
					</v-tooltip>
				</v-btn>

				<v-btn
					v-if="availability.share"
					icon
					size="small"
					variant="text"
					density="comfortable"
					@click="shareResource"
				>
					<v-icon size="13">
						fa: fa-duotone fa-rectangle-history-circle-user
					</v-icon>
					<v-tooltip activator="parent">{{
						t("fileBrowser.properties.shareTooltip")
					}}</v-tooltip>
				</v-btn>

				<v-btn
					v-if="availability.toggleFavorite"
					icon
					size="small"
					variant="text"
					density="comfortable"
					:color="selectedFile.is_favorite ? 'warning' : undefined"
					@click="toggleFavoriteResource"
				>
					<v-icon size="13">fa: fa-solid fa-house</v-icon>
					<v-tooltip activator="parent">{{
						t("fileBrowser.properties.favoriteTooltip")
					}}</v-tooltip>
				</v-btn>

				<v-btn
					v-if="availability.remove"
					icon
					size="small"
					variant="text"
					density="comfortable"
					color="error"
					data-testid="delete-resource"
					@click="removeFile"
				>
					<v-icon size="13">fa: fa-solid fa-trash</v-icon>
					<v-tooltip activator="parent">
						{{ t("fileBrowser.properties.deleteTooltip") }}
					</v-tooltip>
				</v-btn>
			</div>
		</template>
	</div>
</template>

<style scoped lang="scss">
.file-properties {
	display: flex;
	flex-direction: column;
	height: 100%;

	&__header,
	&__footer {
		display: flex;
		align-items: center;
		flex: none;
		background-color: rgba(var(--color-background), 0.6);
	}

	&__header {
		gap: 8px;
	}

	&__icon {
		flex: none;
	}

	&__title {
		flex: 1 1 auto;
		min-width: 0;
		font-size: 13px;
		font-weight: 600;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__close {
		flex: none;
	}

	&__body {
		flex: 1 1 auto;
		min-height: 0;
		overflow-y: auto;
	}
}

.prop {
	display: flex;
	align-items: baseline;
	gap: 8px;
	padding: 3px 0;

	&--stacked {
		flex-direction: column;
		align-items: stretch;
		gap: 2px;

		/* Stacking flips the main axis, which turns the label's 84px column
		   width into an 84px-tall box - and the value then reads as if it had
		   blank lines in front of it. */
		.prop__label {
			flex: none;
		}
	}

	&__label {
		flex: 0 0 84px;
		font-size: 11px;
		color: rgba(var(--color-on-background), 0.7);
	}

	&__value {
		flex: 1 1 auto;
		min-width: 0;
		font-size: 12.5px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;

		&--wrap {
			white-space: normal;
			overflow: visible;
		}

		/* Chips are boxes, not text: they need to wrap and gap rather than
		   ellipsize, and the label column is only 84px wide. */
		&--tags {
			display: flex;
			flex-wrap: wrap;
			gap: 4px;
			white-space: normal;
			overflow: visible;
		}
	}
}
</style>
